<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import SectionLayout from './components/SectionLayout.vue';

export default {
  components: {
    BaseSettingsHeader,
    NextButton,
    AccountId,
    BuildInfo,
    AccountDelete,
    AudioTranscription,
    SectionLayout,
    WithLabel,
    NextInput,
  },
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
      mostAssistantEnabled: false,
      mostInsightsEnabled: false,
      mostAssistantWebhookUrl: '',
      mostAssistantTimeoutSeconds: 30,
      mostAssistantMaxMessages: 500,
      mostAssistantBasePrompt: '',
      mostInsightsWebhookUrl: '',
      mostInsightsTimeoutSeconds: 60,
      mostN8nServerUrl: '',
      mostN8nUsername: '',
      mostN8nPassword: '',
      mostN8nPasswordConfigured: false,
      mostN8nClearPassword: false,
      mostAssistantN8nWebhookPath: '',
      mostInsightsN8nWebhookPath: '',
      n8nSettingsSaving: false,
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAudioTranscriptionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CAPTAIN
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
  },
  watch: {
    'currentAccount.id'(id) {
      if (id) {
        this.initializeAccount();
      }
    },
  },
  mounted() {
    // Account already in the store (navigated in): seed immediately.
    if (this.currentAccount.id) {
      this.initializeAccount();
    }
  },
  methods: {
    async initializeAccount() {
      try {
        const { name, locale, id, domain, support_email, features } =
          this.getAccount(this.accountId);

        const effectiveLocale = this.uiSettings?.locale || locale;
        if (effectiveLocale) {
          this.$root.$i18n.locale = effectiveLocale;
        }
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
        const settings = this.getAccount(this.accountId).settings || {};
        this.mostAssistantEnabled = settings.most_assistant_enabled === true;
        this.mostInsightsEnabled = settings.most_insights_enabled === true;
        this.mostAssistantWebhookUrl = settings.most_assistant_webhook_url || '';
        this.mostAssistantTimeoutSeconds = Number(settings.most_assistant_timeout_seconds || 30);
        this.mostAssistantMaxMessages = Number(settings.most_assistant_max_messages || 500);
        this.mostAssistantBasePrompt = settings.most_assistant_base_prompt || '';
        this.mostInsightsWebhookUrl = settings.most_insights_webhook_url || '';
        this.mostInsightsTimeoutSeconds = Number(settings.most_insights_timeout_seconds || 60);
        this.loadN8nConfiguration();
      } catch (error) {
        // Ignore error
      }
    },

    async loadN8nConfiguration() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/most_n8n_settings`
        );
        const legacyEndpoint = value => {
          try {
            const url = new URL(value);
            return { server: url.origin, path: url.pathname };
          } catch (error) {
            return { server: '', path: '' };
          }
        };
        const legacyAssistant = legacyEndpoint(this.mostAssistantWebhookUrl);
        const legacyInsights = legacyEndpoint(this.mostInsightsWebhookUrl);
        this.mostN8nServerUrl =
          data.server_url || legacyAssistant.server || legacyInsights.server;
        this.mostAssistantN8nWebhookPath =
          data.assistant_webhook_path || legacyAssistant.path;
        this.mostInsightsN8nWebhookPath =
          data.insights_webhook_path || legacyInsights.path;
        this.mostN8nUsername = data.username || '';
        this.mostN8nPasswordConfigured = !!data.password_configured;
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível carregar a configuração do n8n.'
        );
      }
    },

    async updateAccount() {
      if (this.n8nSettingsSaving) return;
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }

      this.n8nSettingsSaving = true;
      const n8nPayload = {
        server_url: this.mostN8nServerUrl.trim(),
        assistant_webhook_path: this.mostAssistantN8nWebhookPath.trim(),
        insights_webhook_path: this.mostInsightsN8nWebhookPath.trim(),
        username: this.mostN8nUsername.trim(),
        clear_password: this.mostN8nClearPassword,
      };
      if (this.mostN8nPassword) n8nPayload.password = this.mostN8nPassword;

      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          most_assistant_enabled: this.mostAssistantEnabled,
          most_insights_enabled: this.mostInsightsEnabled,
          most_assistant_webhook_url: this.mostAssistantWebhookUrl.trim(),
          most_assistant_timeout_seconds: Number(this.mostAssistantTimeoutSeconds || 30),
          most_assistant_max_messages: Number(this.mostAssistantMaxMessages || 500),
          most_assistant_base_prompt: this.mostAssistantBasePrompt.trim(),
          most_insights_webhook_url: this.mostInsightsWebhookUrl.trim(),
          most_insights_timeout_seconds: Number(this.mostInsightsTimeoutSeconds || 60),
        });

        const { data } = await axios.patch(
          `/api/v1/accounts/${this.accountId}/most_n8n_settings`,
          n8nPayload
        );
        this.mostN8nPasswordConfigured = !!data.password_configured;
        this.mostN8nUsername = data.username || '';
        this.mostN8nPassword = '';
        this.mostN8nClearPassword = false;

        const updatedLocale = this.uiSettings?.locale || this.locale;
        if (updatedLocale) this.$root.$i18n.locale = updatedLocale;
        this.getAccount(this.id).locale = this.locale;
        useAlert('Configuração da MOST salva com sucesso.');
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('GENERAL_SETTINGS.UPDATE.ERROR')
        );
      } finally {
        this.n8nSettingsSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
        class="!pt-0"
      >
        <form
          v-if="!uiFlags.isFetchingItem"
          class="grid gap-4"
          @submit.prevent="updateAccount"
        >
          <WithLabel
            name="account-name"
            :has-error="v$.name.$error"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
          >
            <NextInput
              v-model="name"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="v$.name.$touch"
            />
          </WithLabel>
          <WithLabel
            name="site-language"
            :has-error="v$.locale.$error"
            :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
          >
            <select v-model="locale" class="!mb-0 text-sm">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyDomainEnabled"
            name="custom-domain"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
          >
            <NextInput
              v-model="domain"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
            <template #help>
              {{
                featureInboundEmailEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              }}

              {{
                featureCustomReplyDomainEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </template>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyEmailEnabled"
            name="support-email"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
          >
            <NextInput
              v-model="supportEmail"
              type="text"
              class="w-full"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </WithLabel>
          <div>
            <NextButton blue :is-loading="isUpdating" type="submit">
              {{ $t('GENERAL_SETTINGS.SUBMIT') }}
            </NextButton>
          </div>
        </form>
      </SectionLayout>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>
    <SectionLayout
      title="Recursos MOST"
      description="Habilite os módulos contratados pela empresa. O acesso de cada login é definido em Agentes."
    >
      <div class="grid gap-4">
        <label class="flex items-center gap-3">
          <input v-model="mostAssistantEnabled" type="checkbox" />
          <span><strong>MOST Assistente IA</strong> — resumo e contexto inteligente do contato.</span>
        </label>
        <label class="flex items-center gap-3">
          <input v-model="mostInsightsEnabled" type="checkbox" />
          <span><strong>MOST Insights</strong> — inteligência gerencial e análise da operação.</span>
        </label>
        <div class="border-t border-n-weak pt-4 mt-2 grid gap-4">
          <div>
            <strong>Servidor n8n</strong>
            <p class="text-sm text-n-slate-11">Informe a URL base do servidor onde os fluxos estão publicados. Os caminhos abaixo são os webhooks de produção que disparam cada fluxo; o nome exibido no editor do n8n, sozinho, não inicia uma execução.</p>
          </div>
          <NextInput
            v-model="mostN8nServerUrl"
            label="Servidor n8n — URL base"
            placeholder="https://n8n.suaempresa.com.br ou http://cmcdn8n:5678"
          />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NextInput
              v-model="mostN8nUsername"
              label="Usuário para autenticação Basic do webhook (opcional)"
              autocomplete="username"
            />
            <NextInput
              v-model="mostN8nPassword"
              type="password"
              label="Senha para autenticação Basic do webhook (opcional)"
              autocomplete="new-password"
              :placeholder="mostN8nPasswordConfigured ? 'Deixe vazio para manter a senha salva' : 'Informe se o webhook exigir autenticação'"
            />
          </div>
          <p class="text-xs text-n-slate-10">
            A senha é armazenada criptografada e nunca é exibida novamente. O login da interface do n8n não autentica automaticamente um webhook; preencha estes campos se o endpoint exigir Basic Auth.
          </p>
          <label v-if="mostN8nPasswordConfigured" class="flex items-center gap-2 text-sm text-n-slate-11">
            <input v-model="mostN8nClearPassword" type="checkbox" />
            <span>Remover credencial Basic salva</span>
          </label>
          <div class="grid gap-4 border-t border-n-weak pt-4">
            <strong>Fluxo MOST Assistente</strong>
            <NextInput
              v-model="mostAssistantN8nWebhookPath"
              label="Caminho do webhook de produção"
              placeholder="/webhook/most-assistente"
            />
            <div class="grid grid-cols-2 gap-3">
              <NextInput v-model="mostAssistantTimeoutSeconds" type="number" label="Timeout (segundos)" />
              <NextInput v-model="mostAssistantMaxMessages" type="number" label="Máximo de mensagens no histórico" />
            </div>
            <WithLabel label="Instruções base do Assistente">
              <textarea v-model="mostAssistantBasePrompt" rows="5" class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-1" placeholder="Orientações adicionais da empresa para o MOST Assistente..." />
            </WithLabel>
          </div>
          <div v-if="mostInsightsEnabled" class="grid gap-3 border-t border-n-weak pt-4">
            <strong>Fluxo MOST Insights</strong>
            <NextInput
              v-model="mostInsightsN8nWebhookPath"
              label="Caminho do webhook de produção"
              placeholder="/webhook/most-insights"
            />
            <NextInput v-model="mostInsightsTimeoutSeconds" type="number" label="Timeout (segundos)" />
          </div>

        </div>
        <div>
          <NextButton
            blue
            :is-loading="isUpdating || n8nSettingsSaving"
            :disabled="isUpdating || n8nSettingsSaving"
            type="button"
            @click="updateAccount"
          >
            Salvar configuração da MOST
          </NextButton>
        </div>
      </div>
    </SectionLayout>
    <AudioTranscription v-if="showAudioTranscriptionConfig" />
    <AccountId />
    <div v-if="!uiFlags.isFetchingItem && isOnChatwootCloud">
      <AccountDelete />
    </div>
    <BuildInfo />
  </div>
</template>
